import { CommonClientError } from "../../error";
import { DateUtils } from "../../primitive-utils";
import { REGEX_LOCAL_DATE } from "../../primitive/time/base/time-regex";
import { DateTimeRange, LocalDateTimeFunc } from "../date-time";
import { DayOfWeek } from "../day-of-week";
import { LocalTimeFunc } from "../time";
import { LOCAL_DATE_FORMAT } from "../time-format";
import { YearMonthFunc } from "../year-month/year-month";
// @deprecated LocalDateFuncは廃止予定。LocalDateを使用すること。
export class LocalDateFunc {
    v;
    constructor(v) {
        this.v = v;
    }
    static get TODAY() {
        return LocalDateFunc.fromDateJST(new Date());
    }
    static MIN = LocalDateFunc.fromDateJST(new Date("1970-01-01T00:00:00.000Z"));
    static MAX = LocalDateFunc.fromDateJST(new Date("3000-01-01T00:00:00.000Z"));
    static isLocalDate = (v) => {
        return v instanceof LocalDateFunc;
    };
    static of({ year, month, day }) {
        const date = new Date(year, month - 1, day);
        date.setFullYear(year); // new Dateに直接渡すと、yearに2桁以下が指定された際に1900年代として自動補完されてしまうのでsetFullYearで別途渡す。
        if (date.getFullYear() !== year ||
            date.getMonth() + 1 !== month ||
            date.getDate() !== day) {
            const formatedDate = `${year}-${month}-${day}`;
            throw new CommonClientError.InvalidInput(`LocalDate invalid error. input[${formatedDate}]`, "存在しない日付です。");
        }
        return new LocalDateFunc({ year, month, day });
    }
    static parse(text, format = LOCAL_DATE_FORMAT) {
        const d = DateUtils.fromFormat(text, format);
        if (!DateUtils.isValidDate(d)) {
            throw new CommonClientError.InvalidInput(`LocalDate parse error. format[${format}], input[${text}]`, "日付の形式が不正です。");
        }
        return LocalDateFunc.of({
            year: d.getFullYear(),
            month: d.getMonth() + 1,
            day: d.getDate(),
        });
    }
    // 正規表現からParseする。
    // ただし、数字部分が yyyy MM dd の順番であることを前提とする。
    static parseFromRegex(text, regex = REGEX_LOCAL_DATE) {
        if (!regex.test(text)) {
            throw new CommonClientError.InvalidInput(`LocalDate regex match error. regex[${regex.source}], input[${text}]`, "日付の形式が不正です。");
        }
        const extractRegex = /\d+/g; // 数字を抽出
        const [year, month, day] = text.match(extractRegex)?.map(Number) ?? [];
        return LocalDateFunc.of({
            year,
            month,
            day,
        });
    }
    /** DateをJST解釈のLocalDateに変換して返す */
    static fromDateJST(v) {
        const jstDate = DateUtils.utcToJSTTime(v);
        return this.fromDate(jstDate);
    }
    // USAGE: localDateArray.sort(LocalDateFunc.compere);
    // 日の昇順 1/1, 1/2, 1/3... で並ぶ
    static compare(a, b) {
        return a.toDate().getTime() - b.toDate().getTime();
    }
    // 日の降順 1/3,1/2,1/1... で並ぶ
    static compareDesc(a, b) {
        return b.toDate().getTime() - a.toDate().getTime();
    }
    countDays(to) {
        return DateUtils.differenceInDays(to.toDate(), this.toDate());
    }
    static equals(a, b) {
        return a.toDate().getTime() === b.toDate().getTime();
    }
    toDateJST() {
        return DateUtils.JSTTimeToUtc(this.toDate());
    }
    getDayOfWeek() {
        const d = this.toDate();
        return DayOfWeek.fromNumber(DateUtils.getDay(d));
    }
    getYearMonth() {
        return YearMonthFunc.of(this.v);
    }
    /** その日の0:00(JST)時点のDateを返す */
    atStartOfDayJST() {
        const d = this.toDate();
        return DateUtils.startOfDayJST(d);
    }
    /** その日の23:59(JST)時点のDateを返す */
    atEndOfDayJST() {
        const d = this.toDate();
        return DateUtils.endOfDayJST(d);
    }
    toDateTimeRange() {
        return DateTimeRange.between({
            startAt: this.atStartOfDay(),
            endAt: this.addDays(1).atStartOfDay(),
        });
    }
    addDays(days) {
        return this.callAsDate((d) => DateUtils.addDays(d, days));
    }
    subDays(days) {
        return this.callAsDate((d) => DateUtils.subDays(d, days));
    }
    addMonths(months) {
        return this.callAsDate((d) => DateUtils.addMonths(d, months));
    }
    subMonths(months) {
        return this.callAsDate((d) => DateUtils.subMonths(d, months));
    }
    addYears(years) {
        return this.callAsDate((d) => DateUtils.addYears(d, years));
    }
    subYears(years) {
        return this.callAsDate((d) => DateUtils.subYears(d, years));
    }
    setDay(dayOfMonth) {
        return LocalDateFunc.of({ ...this.v, day: dayOfMonth });
    }
    atTime(time) {
        return LocalDateTimeFunc.of({ ...this.v, ...time.toValues() });
    }
    atStartOfDay() {
        return this.atTime(LocalTimeFunc.of({ hour: 0 }));
    }
    startOfMonth() {
        return this.callAsDate((d) => DateUtils.startOfMonth(d));
    }
    endOfMonth() {
        return this.callAsDate((d) => DateUtils.endOfMonth(d));
    }
    // 次の○日
    next(dayOfMonth) {
        return this.v.day <= dayOfMonth
            ? this.setDay(dayOfMonth)
            : this.setDay(dayOfMonth).addMonths(1);
    }
    // 前の○日
    previous(dayOfMonth) {
        return this.v.day > dayOfMonth
            ? this.setDay(dayOfMonth)
            : this.setDay(dayOfMonth).subMonths(1);
    }
    // 次の○曜日
    nextDay(dayOfWeek) {
        return this.callAsDate((d) => DateUtils.nextDay(d, dayOfWeek.getNumber()));
    }
    // 前の○曜日
    previousDay(dayOfWeek) {
        return this.callAsDate((d) => DateUtils.previousDay(d, dayOfWeek.getNumber()));
    }
    equals(other) {
        return LocalDateFunc.equals(this, other);
    }
    isAfter(other) {
        return this.toDate().getTime() > other.toDate().getTime();
    }
    isEqualOrAfter(other) {
        return this.equals(other) || this.isAfter(other);
    }
    isBefore(other) {
        return this.toDate().getTime() < other.toDate().getTime();
    }
    isEqualOrBefore(other) {
        return this.equals(other) || this.isBefore(other);
    }
    toString() {
        const d = this.toDate();
        return DateUtils.format(d, LOCAL_DATE_FORMAT);
    }
    toValues() {
        return this.v;
    }
    format(format = LOCAL_DATE_FORMAT) {
        return DateUtils.format(this.toDate(), format, true);
    }
    // Dateで受け付ける関数を呼びたいときのアダプタ
    callAsDate(func) {
        const d = this.toDate();
        const result = func(d);
        return LocalDateFunc.fromDate(result);
    }
    toDate() {
        const date = new Date(this.v.year, this.v.month - 1, this.v.day);
        date.setFullYear(this.v.year); // new Dateに直接渡すと、yearに2桁以下が指定された際に1900年代として自動補完されてしまうのでsetFullYearで別途渡す。
        return date;
    }
    static fromDate(d) {
        return LocalDateFunc.of({
            year: d.getFullYear(),
            month: d.getMonth() + 1,
            day: d.getDate(),
        });
    }
}
//# sourceMappingURL=local-date.js.map